import { Box } from "@mui/material";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_MARKETING_TOP_CHANNELS,
  CHART_KEY_DASHBOARD_MARKETING_TRAFFIC_TABLE,
} from "features/analytics/constants/chartKeys";
import {
  ApiDimension,
  ApiChartTemplate,
  ApiChartType,
  ApiMetric,
  ApiChartAttribute,
} from "@incendium/api";
import { useMemo, useState } from "react";
import { useIsMobile } from "Hooks/useIsMobile";
import {
  dashboardChartKey,
  useIsEcommerceProject,
  withoutAddToCartMetrics,
} from "features/dashboard/utils/ecommerceMetrics";

interface IMarketingTabProps {
  attributes: ApiChartAttribute[];
}

function MarketingTab({ attributes }: IMarketingTabProps) {
  const isEcommerce = useIsEcommerceProject();
  const [metric, setMetric] = useState<ApiMetric>(ApiMetric.METRIC_REVENUE);
  const isMobile = useIsMobile();

  const tableFields = useMemo(
    () =>
      withoutAddToCartMetrics(
        [
          ApiMetric.METRIC_SESSIONS_COUNT,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
          ApiMetric.METRIC_RETURNING_LEAD_PERCENTAGE,
          ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE,
          ApiMetric.METRIC_REVENUE,
        ],
        isEcommerce,
      ),
    [isEcommerce],
  );

  return (
    <>
      <Box sx={{ height: 400, flexShrink: 0 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_MARKETING_TOP_CHANNELS}
          staticAttributes={attributes}
          onChangeMetric={(m) => setMetric(m)}
          chart={{
            name: `Top 5 Channels by`,
            dimension: [
              ApiDimension.DIMENSION_SESSION_START_BY_DAY,
              ApiDimension.DIMENSION_CHANNEL,
            ],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            limit: 5,
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [metric],
              },
            ],
            displayOptions: {
              noFilters: true,
              availableMetrics: [
                ApiMetric.METRIC_REVENUE,
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                ApiMetric.METRIC_MACRO_CONVERSION_RATE,
              ],
            },
          }}
        />
      </Box>
      <Box sx={{ height: 600, flexShrink: 0 }}>
        <StandardAnalyticsCard
          chartKey={dashboardChartKey(
            CHART_KEY_DASHBOARD_MARKETING_TRAFFIC_TABLE,
            isEcommerce,
          )}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Traffic & Performance by ",
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            comparison: true,
            yAxisKeys: [
              {
                key: "l",
                fields: tableFields,
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              rowsPerPage: 0,
              showTotals: true,
              availableDimensions: [
                ApiDimension.DIMENSION_CHANNEL,
                ApiDimension.DIMENSION_SOURCE,
                ApiDimension.DIMENSION_MEDIUM,
                ApiDimension.DIMENSION_CAMPAIGN,
                ApiDimension.DIMENSION_CONTENT,
                ApiDimension.DIMENSION_TERM,
              ],
              tableColWidths: {
                [ApiDimension.DIMENSION_CHANNEL]: isMobile ? 200 : 400,
                [ApiDimension.DIMENSION_SOURCE]: 125,
                [ApiDimension.DIMENSION_MEDIUM]: 125,
                [ApiDimension.DIMENSION_CAMPAIGN]: 125,
                [ApiDimension.DIMENSION_CONTENT]: 125,
                [ApiDimension.DIMENSION_TERM]: 125,
              },
            },
          }}
        />
      </Box>
    </>
  );
}

export default MarketingTab;
